import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Circle } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shared/utils/utils';
import { useHolidays } from '@/features/hr/hooks/useHr';
import type { Meeting, BusyBlock } from '../types';

interface MeetingCalendarProps {
  month: Date;                       // any date within the displayed month
  meetings: Meeting[];
  ceoBusy: BusyBlock[];
  ceoName?: string;
  onMonthChange: (next: Date) => void;
  onSelectDate: (isoDate: string) => void;
  onOpenMeeting: (m: Meeting) => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = iso(new Date());

/**
 * A full month calendar. Each day cell shows the meetings scheduled that day and
 * a marker when the CEO (Super Admin) is busy, so users can spot free days before
 * requesting a slot. Clicking a day opens the scheduler prefilled with that date.
 */
export function MeetingCalendar({
  month, meetings, ceoBusy, ceoName, onMonthChange, onSelectDate, onOpenMeeting,
}: MeetingCalendarProps) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  // Construct the Sunday grid start directly (no post-construction mutation) and
  // extract a primitive timestamp so the memo dependency stays stable/checkable.
  const gridStartTime = new Date(month.getFullYear(), month.getMonth(), 1 - first.getDay()).getTime();
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStartTime);
    d.setDate(d.getDate() + i);
    return d;
  }), [gridStartTime]);

  // Fetch holidays for the displayed year
  const { data: holidays = [] } = useHolidays(month.getFullYear());
  const holidayMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const h of holidays) map.set(h.date, h.name);
    return map;
  }, [holidays]);

  // Index meetings + CEO-busy days by ISO date for quick lookup.
  const byDay = useMemo(() => {
    const m = new Map<string, Meeting[]>();
    for (const mt of meetings) {
      const key = mt.meeting_date ?? (mt.scheduled_at ? mt.scheduled_at.slice(0, 10) : '');
      if (!key) continue;
      (m.get(key) ?? m.set(key, []).get(key)!).push(mt);
    }
    return m;
  }, [meetings]);

  const ceoBusyDays = useMemo(() => {
    const s = new Set<string>();
    for (const b of ceoBusy) s.add(new Date(b.start).toISOString().slice(0, 10));
    return s;
  }, [ceoBusy]);

  const monthLabel = first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="flex flex-1 flex-col rounded-card border border-outline bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-outline px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-data-lg font-semibold text-on-surface">{monthLabel}</h2>
          {ceoName && (
            <span className="hidden items-center gap-1 text-body-sm text-on-surface-variant sm:inline-flex">
              <Circle className="h-2.5 w-2.5 fill-warning text-warning" /> {ceoName} busy
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onMonthChange(new Date())}>Today</Button>
          <Button variant="ghost" size="sm" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Weekday row */}
      <div className="grid grid-cols-7 border-b border-outline">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-1.5 text-center text-label-caps uppercase text-on-surface-variant">{w}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid flex-1 grid-cols-7 grid-rows-6">
        {days.map((d) => {
          const key = iso(d);
          const inMonth = d.getMonth() === first.getMonth();
          const isToday = key === todayIso;
          const dayMeetings = byDay.get(key) ?? [];
          const ceoBusyDay = ceoBusyDays.has(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectDate(key)}
              className={cn(
                'group flex min-h-[84px] flex-col gap-1 border-b border-r border-outline p-1.5 text-left transition-colors hover:bg-surface-variant',
                !inMonth && 'bg-surface-container-low/50 text-on-surface-variant',
                holidayMap.has(key) && inMonth && 'bg-danger/5',
              )}
            >
              <div className="flex items-center justify-between">
                <span className={cn(
                  'inline-flex h-6 w-6 items-center justify-center rounded-full text-body-sm',
                  isToday ? 'bg-primary font-semibold text-on-primary' : 'text-on-surface',
                )}>
                  {d.getDate()}
                </span>
                <div className="flex items-center gap-1">
                  {holidayMap.has(key) && (
                    <span className="text-[9px] font-bold text-danger" title={holidayMap.get(key)}>H</span>
                  )}
                  {ceoBusyDay && <Circle className="h-2 w-2 fill-warning text-warning" aria-label="CEO busy" />}
                </div>
              </div>
              {holidayMap.has(key) && (
                <span className="truncate text-[9px] font-medium text-danger" title={holidayMap.get(key)}>
                  {holidayMap.get(key)}
                </span>
              )}
              <div className="flex flex-col gap-0.5 overflow-hidden">
                {dayMeetings.slice(0, 3).map((mt) => (
                  <span
                    key={mt.id}
                    onClick={(e) => { e.stopPropagation(); onOpenMeeting(mt); }}
                    className="truncate rounded bg-primary-container/70 px-1.5 py-0.5 text-data-xs text-on-primary-container hover:bg-primary-container"
                    title={`${mt.meeting_time ?? ''} ${mt.title}`}
                  >
                    {mt.meeting_time ? `${mt.meeting_time} ` : ''}{mt.title}
                  </span>
                ))}
                {dayMeetings.length > 3 && (
                  <span className="px-1 text-data-xs text-on-surface-variant">+{dayMeetings.length - 3} more</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
